import { useRef, useState, type KeyboardEvent } from "react";
import { salesMailto } from "../config/public";
import "./MarketingStorefrontStory.css";

const story = [
  { file: "01-comment-to-order", title: "From comment to order", description: "Give an interested customer a clear path to your storefront.", alt: "A business owner taking a break while the configured storefront journey stays available" },
  { file: "02-customer-comments", title: "A customer comments", description: "The customer comments your configured keyword, such as SHOP.", alt: "A customer comments SHOP on a handbag post while the LeadCat cat watches" },
  { file: "03-brand-replies", title: "Your brand replies", description: "LeadCat sends your configured reply and storefront link for eligible matches.", alt: "Your Brand replies publicly and sends a plain-text storefront link in a private message, powered by LeadCat" },
  { file: "04-customer-shops", title: "They explore your storefront", description: "The customer opens your branded storefront and chooses what to buy.", alt: "The customer browses a classic handbag on a branded LeadCat storefront" },
  { file: "05-payment-confirmed", title: "They complete payment", description: "With online buying and payments enabled, they can check out on your storefront.", alt: "An illustrative payment confirmation for a storefront with online payments enabled" },
  { file: "06-order-received", title: "You receive the order", description: "Review the order in your workspace and take care of fulfilment.", alt: "The business owner sees a new order and a LeadCat Storefront invitation" },
] as const;

const whatsappStory = [
  { file: "wa-01-whatsapp-to-order", title: "From WhatsApp to order", description: "Let your AI assistant help customers take the next step toward buying.", alt: "The business owner and LeadCat cat introduce the WhatsApp-to-order journey" },
  { file: "wa-02-ai-bot-replies", title: "Your AI bot replies", description: "Your OpenAI-connected WhatsApp bot welcomes the customer.", alt: "Your Brand’s AI WhatsApp assistant greets a customer who sends Hi" },
  { file: "wa-03-product-link-sent", title: "The product link is shared", description: "The conversation continues with a link to your storefront product or catalogue.", alt: "Your Brand’s WhatsApp assistant shares a plain-text storefront link for the Classic Handbag" },
  ...story.slice(3),
];

type Journey = "instagram" | "whatsapp";
const journeys = [
  { id: "instagram", label: "Instagram comments" },
  { id: "whatsapp", label: "WhatsApp AI assistant" },
] as const;

export function MarketingStorefrontStory() {
  const [journey, setJourney] = useState<Journey>("instagram");
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const whatsapp = journey === "whatsapp";
  const activeStory = whatsapp ? whatsappStory : story;

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? 1 : (index + 1) % 2;
    setJourney(journeys[nextIndex]!.id);
    tabs.current[nextIndex]?.focus();
  }

  return (
    <section className="pm-storefront-story" id="storefront-story" aria-labelledby="pm-storefront-story-title">
      <header className="pm-section-heading">
        <span>The optional LeadCat Storefront</span>
        <h2 id="pm-storefront-story-title">{whatsapp ? "See how a WhatsApp chat becomes an order." : "See how a comment becomes an order."}</h2>
        <p>{whatsapp ? "Your OpenAI-connected WhatsApp assistant helps customers find products and continue to your storefront." : "Less back-and-forth for your team. A clearer path to buying for your customer."}</p>
      </header>
      <div className="pm-story-tabs" role="tablist" aria-label="Choose a storefront journey">
        {journeys.map((item, index) => (
          <button key={item.id} ref={(element) => { tabs.current[index] = element; }} id={`pm-story-tab-${item.id}`} type="button" role="tab" aria-selected={journey === item.id} aria-controls="pm-story-panel" tabIndex={journey === item.id ? 0 : -1} onClick={() => setJourney(item.id)} onKeyDown={(event) => onTabKeyDown(event, index)}>{item.label}</button>
        ))}
      </div>
      <div id="pm-story-panel" role="tabpanel" aria-labelledby={`pm-story-tab-${journey}`}>
      <p className="pm-story-swipe">Swipe to follow the journey <span aria-hidden="true">→</span></p>
      <div key={journey} className="pm-story-scroll" role="region" aria-label="Storefront journey slides" tabIndex={0}>
        <ol className="pm-story-grid">
          {activeStory.map((step, index) => (
            <li key={step.file}>
              <a className="pm-story-image-link" href={`${import.meta.env.BASE_URL}assets/storefront-story/${step.file}.webp`} target="_blank" rel="noreferrer" aria-label={`View slide ${index + 1}: ${step.title}`}>
                <img src={`${import.meta.env.BASE_URL}assets/storefront-story/${step.file}.webp`} width="1024" height="1024" loading="lazy" decoding="async" alt={step.alt} />
              </a>
              <div className="pm-story-copy"><span>STEP {index + 1} OF 6</span><h3>{step.title}</h3><p>{step.description}</p></div>
            </li>
          ))}
        </ol>
      </div>
      <div className="pm-story-footer">
        <p>{whatsapp ? "Illustrative journey. Requires your configured WhatsApp bot, connected business account and a storefront with online buying and payments enabled." : "Illustrative journey. Requires configured automation, eligible connected accounts and a storefront with online buying and payments enabled."}</p>
        <a className="pm-button pm-button-primary" href={whatsapp ? salesMailto("LeadCat AI WhatsApp bot demo") : "/social-commerce/"}>{whatsapp ? "Ask about the WhatsApp bot" : "Explore Storefront"} <span aria-hidden="true">→</span></a>
      </div>
      </div>
    </section>
  );
}
